/**
 * Created by Yoyo Fang on 06/12/2020.
 * Description:
 *
 * ------ maintenance history ------
 */

import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd, NavigationStart } from '@angular/router';
import { Subject } from 'rxjs';
import { Subscription } from 'rxjs/Subscription';
import { take, filter } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import {
    EnumAaclTreeListNodeClickables, EnumAaclTreeListNodeClickHandlers,
    EnumAaclTreeListNodeHighlightMode, EnumAaclTreeListActionType
} from 'aacl-tree-list';
import { IntralinksAccountDialogService } from '../../../components/intralinks-account-dialog/intralinks-account-dialog.service';
import { IntralinksAccountDialogOpenParam, IntralinksAccountDialogOperations } from '../../../components/intralinks-account-dialog/intralinks-account-dialog.view-model';
import { AppState } from '../../../redux';
import { visibleScreensSelector } from '../../../redux/reducers/system.reducer';
import { IntralinksLoadWorkspacesFolders, IntralinksLoadFolderContent, IntralinksDeleteAction, INTRALINKS_ACTIONS } from '../../../tamalelibs/redux/intralinks.actions';
import { getFolderStructureState, getFolderContentState, getPageStatusState } from '../../../tamalelibs/redux/intralinks.reducer';
import { IntralinksGroupContainerConfig, IntralinksGroupContainerReloadAction, IntralinksGroupContainerReadyFeedback, IntralinksGroupContainerUpdateFileAction } from '../../../components/intralinks-group/intralinks-group.model';
import { ToastService } from '../../../widgets/toast/toast.service';
import { NotificationOptions, NotificationStyles } from '../../../widgets/notification/notification.model';
import {
    IntralinksContentContainerConfig, IntralinksContentContainerReloadAction,
    IntralinksContentContainerReloadActionPayload, IntralinksContentType
} from '../../../components/intralinks-content-container/intralinks-content-container.model';
import { IntralinksUIService } from './intralinks-ui.service';
import { ContextMenuConfig, ContextMenuShowAction } from '../../../widgets/tam-context-menu/tam-context-menu.model';
import { AlertBtnTypes, AlertButton } from '../../../widgets/alert-window/alert-window.model';
import { AlertWindowService } from '../../../widgets/alert-window/alert-window.service';
import { IntralinksValidationService } from '../../../components/intralinks-validation/intralinks-validation.service';
import { IntralinksAccountInfo } from '../../../tamalelibs/models/intralinks.model';
import { StoreQuerierService } from '../../../services/store-querier.service';
import { IntralinksMappingDialogService } from '../../../components/intralinks-mapping-dialog/intralinks-mapping-dialog.service';
import { IntralinksMappingDialogParam } from '../../../components/intralinks-mapping-dialog/intralinks-mapping-dialog.view-model';
import * as _ from 'lodash';
import { IntralinksSessionService } from '../../../services/intralinks-session.service';
import { businessConstants } from '../../../constants/business.constants';
import { StringLiteralsPipe } from '../../../pipes/translate.pipe';

@Component({
    selector: 'tam-intralinks',
    templateUrl: './intralinks.component.html',
    styleUrls: ['./intralinks.component.scss'],
})
export class IntralinksComponent implements OnInit, OnDestroy {
    id: string;
    folderId: string;
    timeFilter: string;
    timeValue: string;
    timeDefaultValue = '1m';
    timeUnit: string;
    keyword: string;
    name: string;
    data: IntralinksAccountInfo;
    treeListOptions: any;
    loading = { tree: true, content: true };
    contentContainerConfig: IntralinksContentContainerConfig;
    isPPCUser = false;
    // files: IntralinksFile[] = null;
    groupConfig: IntralinksGroupContainerConfig;
    screens: Array<any> = [];
    workspaceId: string;
    screenEditable = false;
    screenDeletable = false;
    contextMenuConfig: ContextMenuConfig;
    private _skipNodeHightlight = false;
    private _actionSubject$ = new Subject();
    private _feedbackSubject$ = new Subject();
    private _stackedInitContent = false;
    private _stackedInitTree = false;
    private _feedbackSubscription: Subscription = null;
    private _destroySubscriptions: Array<Subscription> = [];

    constructor(
        private _store: Store<AppState>,
        private _changeDetectorRef: ChangeDetectorRef,
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _intralinksAccountDialogService: IntralinksAccountDialogService,
        private _toastService: ToastService,
        private _intralinksService: IntralinksUIService,
        private _alertWindow: AlertWindowService,
        private _validationService: IntralinksValidationService,
        private _storeQuerier: StoreQuerierService,
        private _intralinksMappingDialogService: IntralinksMappingDialogService,
        private _intralinksSessionService: IntralinksSessionService
    ) {
        this.id = this._activatedRoute.snapshot.params['id'];
        if (!this.id) {
            this._router.navigate(['/error']);
        } else {
            this.loading.tree = true;
            this._initAccount(this.id);

            // Initialize folder details if navigating to a specific folder
            if (this._activatedRoute.snapshot.firstChild) {
                this._navigateToFolderDetail();
            }
        }
        // watch for url changes
        this._destroySubscriptions.push(
            this._router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(e => {
                const newId = this._activatedRoute.snapshot.params['id'];
                if (this.id !== newId && newId) {
                    // navigating to a different account
                    this.id = newId;
                    this.loading.tree = true;
                    this._stackedInitTree = true;
                    this._initAccount(this.id);
                }
                // here is need to add else?
                if (this._activatedRoute.snapshot.firstChild) {
                    this._navigateToFolderDetail();
                } else {
                    // navigating to account root
                    this.folderId = null;
                    this.timeFilter = null;
                    this.keyword = null;
                    this.loading.content = false;
                    this._validationService.breakByNavigation();
                    this._collapseAllTreeNodes();
                }
                if (!this.id) {
                    this._router.navigate(['/error']);
                }
            }),
        );
    }

    ngOnInit() {

        const currentUser = this._storeQuerier.getCurrentUser();
        // PPC user can save
        this.isPPCUser = currentUser.credential.publishPubConfiguration;
        this.contentContainerConfig = new IntralinksContentContainerConfig();

        this.contextMenuConfig = new ContextMenuConfig();
        this.contextMenuConfig.stateful = false;

        this._intralinksSessionService.addListener();

        // init left hand tree
        this._destroySubscriptions.push(
            this._store.pipe(
                select(visibleScreensSelector),
                filter(screens => !!screens && screens.length > 0)
            ).subscribe((screens: Array<any>) => {
                this.screens = screens;
                this._initAccount(this.id);
            })
        );

        // on left hand tree data ready
        this._destroySubscriptions.push(
            this._store.pipe(
                select(getFolderStructureState),
            ).subscribe((result: any) => {
                if (result['workflow'] === 'reload' && result['unloaded'] === 0) {
                    const folders = result['folders'];
                    const customizeClicks = new Map<EnumAaclTreeListNodeClickables, Array<EnumAaclTreeListNodeClickHandlers>>();
                    customizeClicks.set(EnumAaclTreeListNodeClickables.ToggleIcon, [EnumAaclTreeListNodeClickHandlers.ExpandCollapse]);
                    customizeClicks.set(EnumAaclTreeListNodeClickables.Text, [
                        EnumAaclTreeListNodeClickHandlers.ExpandCollapse,
                        EnumAaclTreeListNodeClickHandlers.Custom,
                        EnumAaclTreeListNodeClickHandlers.Focus
                    ]);
                    const mapResult = this._mapStoreFolders(folders, this.folderId);
                    const updatedFolders = this._addRecentDocumentsOption(mapResult.folders, false);
                    this.treeListOptions = {
                        items: updatedFolders,
                        maxTreeLevel: 20,
                        levelPadding: 10,
                        customizeClicks: customizeClicks,
                        collapseAllOnLoaded: true,
                        actionSubject$: this._actionSubject$,
                        feedbackSubject$: this._feedbackSubject$,
                        enableTreeNodeFilter: false,
                        nodeHighlightMode: EnumAaclTreeListNodeHighlightMode.Row,
                        customClickHandler: this._treeListHandler.bind(this),
                        beforeExpandCollapseNode: (node) => this._splashMfaValidate(node.data)
                        // filterSearchBoxPlaceholder: 'Search a field'
                    };

                    // mapping dialog only make sense when tree list has workspaces/folders
                    if (this.treeListOptions && this.treeListOptions.items && this.treeListOptions.items.length) {
                        if (this.contextMenuConfig.items && this.contextMenuConfig.items.length === 2) {
                            this.contextMenuConfig.items.push(
                                { divider: true },
                                { id: '2', text: 'Configure Mapping', disabled: !this.screenDeletable }
                            );
                        }
                    }

                    this.loading.tree = false;
                    if (!this.folderId && !this.timeFilter) {
                        // // redirection to first workspace if missing folder Id && is not account level recently added page
                        // if (updatedFolders.length > 0 && updatedFolders[0].items && updatedFolders[0].items.length > 0) {
                        //     this._goToFolder(null, updatedFolders[0].items[0].workspaceId);
                        // }
                        const payload = new IntralinksContentContainerReloadActionPayload(IntralinksContentType.EMPTY, null);
                        const action = new IntralinksContentContainerReloadAction(this.id, this.workspaceId, payload);
                        this.contentContainerConfig.actionSubject$.next(action);
                        this.loading.content = false;
                    } else if (this._stackedInitContent) {
                        this._stackedInitContent = false;
                        this._initContent();
                    }
                } else if (result['workflow'] === 'submit') {
                    if (this.groupConfig && this.timeFilter) {
                        const reloadAction = new IntralinksGroupContainerUpdateFileAction();
                        reloadAction.payload = result['file'];
                        this.groupConfig.actionSubject$.next(reloadAction);
                    }
                } else if (result['error']) {
                    const msg = result['error']['message'] || result['error'];

                    if (result['session'] !== true) {
                        // error notification when session timeout
                        this._toast(NotificationStyles.Error, msg);

                        // UI status update when session timeout
                        this.treeListOptions = null;
                        this.loading.tree = false;
                        this.loading.content = false;
                        const payload = new IntralinksContentContainerReloadActionPayload(IntralinksContentType.EMPTY, null);
                        const action = new IntralinksContentContainerReloadAction(this.id, null, payload);
                        this.contentContainerConfig.actionSubject$.next(action);
                    }
                }
            })
        );

        // listener for result of _loadFolderContent()
        this._destroySubscriptions.push(
            this._store.pipe(
                select(getFolderContentState),
                filter((files) => files)
            ).subscribe((files: Array<any>) => {
                // this.files = files;
                if (!this.timeFilter && this.workspaceId) {
                    const payload = new IntralinksContentContainerReloadActionPayload(IntralinksContentType.FOLDER, files);
                    const action = new IntralinksContentContainerReloadAction(this.id, this.workspaceId, payload);
                    this.contentContainerConfig.actionSubject$.next(action);
                    this.loading.content = false;
                }
            })
        );

        this._destroySubscriptions.push(
            this._store.pipe(
                select(getPageStatusState)
            ).subscribe(status => {
                if (status && status.workflow === 'delete') {
                    if (status.result) {
                        this._toast(NotificationStyles.Success, 'Intralinks settings is deleted.');
                    } else {
                        this._toast(NotificationStyles.Error, status.error);
                    }
                    if (status.result) {
                        this._router.navigate(['/']);
                    }
                }
            })
        );

        // watch for url changes
        this._destroySubscriptions.push(
            this._router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((e: NavigationStart) => {
                const trimed = e.url.replace('/feeds/intralinks/', '');
                if (trimed !== e.url) {
                    const index = trimed.indexOf('/');
                    let id = trimed;
                    if (index >= 0) {
                        id = id.substring(0, index);
                    }
                    if (this.id !== id) {
                        this.loading.tree = true;
                        this._stackedInitTree = true;
                    }
                    // here is need to add the else
                }
            }));

    }

    ngOnDestroy() {
        this.destroyComponent();
        this._intralinksSessionService.removeListener();

        this._store.dispatch({ type: INTRALINKS_ACTIONS.CLEAR_FOLDER_CACHE });
    }

    destroyComponent() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
        if (this._feedbackSubscription && !this._feedbackSubscription.closed) {
            this._feedbackSubscription.unsubscribe();
        }
    }

    editAccount() {
        const param = new IntralinksAccountDialogOpenParam();
        param.operation = IntralinksAccountDialogOperations.EDIT;
        param.screenId = this.id;
        param.data = this.data;
        this._intralinksAccountDialogService.open(param);
    }

    deleteAccount() {
        const content: string[] = [];
        content.push('Are you sure you want to delete this intralinks account setting?');
        const cancelBtn: AlertButton = {
            text: 'Go Back',
            type: AlertBtnTypes.tertiary
        };
        const confirmButton: AlertButton = {
            text: 'Delete',
            type: AlertBtnTypes.destructive
        };
        const subscription = this._alertWindow.
            custom('Delete confirmation required', content, cancelBtn, confirmButton)
            .subscribe((result: boolean) => {
                if (result) {
                    const action = new IntralinksDeleteAction();
                    action.payload = { screenId: this.id };
                    this._store.dispatch(action);
                }
                subscription.unsubscribe();
            });
    }

    mappingConfiguration() {
        if (this.treeListOptions && this.treeListOptions.items && this.treeListOptions.items.length > 0) {
            const param = new IntralinksMappingDialogParam();
            const treeItems = _.cloneDeep(this.treeListOptions.items);
            if (treeItems && treeItems.length > 1) {
                treeItems.shift();
            }
            param.treeItems = treeItems.map(folder => {
                folder.items = Array.isArray(folder.items) ? folder.items.filter(item => item.folderType !== 'link') : [];
                return folder;
            });
            param.account = this.data;
            param.selectedFolderId = this.folderId;
            param.screenId = this.id;
            this._intralinksMappingDialogService.open(param);
        }
    }

    private _treeListHandler(node) {
        this._skipNodeHightlight = true;
        if (node.data && node.data.folderId) {
            // on selected node is expanded already and is not activate
            if (!node.isCollapsed && !node.isFocused) {
                setTimeout(() => {
                    this._expandToChildNode(node.data.folderId);
                });
            }
            if (node.data.folderId !== this.folderId || this.timeUnit) {
                // this._loadFolderContent(node.data.folderId, node.data);
                const payload = new IntralinksContentContainerReloadActionPayload(IntralinksContentType.LOADING, null);
                const action = new IntralinksContentContainerReloadAction(this.id, node.data.workspaceId, payload);
                this.contentContainerConfig.actionSubject$.next(action);
                this._goToFolder(this.folderId, node.data.folderId);
            }
        } else if (node.data && node.data.folderType === 'link') {
            // same url won't trigger NavigationEnd => page won't refresh => should not reset content
            if (this.folderId !== node.data.workspaceId || this.timeFilter !== this.timeDefaultValue) {
                const payload = new IntralinksContentContainerReloadActionPayload(IntralinksContentType.LOADING, null);
                const action = new IntralinksContentContainerReloadAction(this.id, node.data.workspaceId, payload);
                this.contentContainerConfig.actionSubject$.next(action);
            }
            this._goToRecent(node.data.workspaceId, this.timeDefaultValue);
        }
    }

    private _isWorkspace(node) {
        return node.folderType === 'workspace';
    }

    private _goToFolder(fromFolderId, toFolderId) {
        if (!this.timeFilter) {
            if (fromFolderId !== toFolderId) {
                this._router.navigate(['folder', toFolderId], { relativeTo: this._activatedRoute });
            }
        } else {
            this._router.navigate(['folder', toFolderId], { relativeTo: this._activatedRoute });
        }
    }

    private _goToRecent(workspaceId, timeFilter) {
        const rootPath = workspaceId ? ['folder', workspaceId] : [];
        this._router.navigate([...rootPath, 'recents', timeFilter], { relativeTo: this._activatedRoute });
    }

    private _getNodeByFolderId(nodes: any[], folderId): any {
        let result = null;
        nodes.forEach(node => {
            if (node.folderId === folderId) {
                result = node;
            } else if (node.items && node.items.length > 0) {
                const subResult = this._getNodeByFolderId(node.items, folderId);
                if (subResult !== null) {
                    result = subResult;
                }
            }
        });
        return result;
    }

    private _mapStoreFolders(folders: any[], activeNodeId: string) {
        const result = {
            includeActiveNode: false,
            folders: []
        };
        if (folders && folders.length > 0) {
            folders.forEach((folder) => {
                const subResult = this._mapStoreFolders(folder.folders, activeNodeId);
                const isActive = folder.id === activeNodeId;
                result.folders.push({
                    displayText: folder.name,
                    folderId: folder.id,
                    workspaceId: folder.workspaceId,
                    createdDate: folder.createdDate,
                    createdBy: folder.createdBy,
                    lastModifiedDate: folder.lastModifiedDate,
                    lastModifiedBy: folder.lastModifiedBy,
                    folderType: folder.type,
                    allowChildren: folder.folders && folder.folders.length > 0,
                    isExpanded: subResult.includeActiveNode,
                    expanded: subResult.includeActiveNode,
                    link: null,
                    isFocused: isActive,
                    isActive: isActive,
                    isSelected: isActive,
                    items: subResult.folders
                });
                result.includeActiveNode = isActive || subResult.includeActiveNode || result.includeActiveNode;
            });
        }
        return result;
    }

    private _generateFilesInWorkspace(items, folderId) {
        let result = [];
        if (items) {
            items.forEach(item => {
                if (item.folderId === folderId) {
                    result = item.items
                        .filter(subitem => subitem.folderType !== 'link')
                        .map((subitem) => {
                            return {
                                name: subitem.displayText,
                                id: subitem.folderId,
                                entityType: 'folder',
                                createdDate: subitem.createdDate,
                                createdBy: subitem.createdBy,
                                lastModifiedDate: subitem.lastModifiedDate,
                                lastModifiedBy: subitem.lastModifiedBy
                            };
                        });
                }
            });
        }
        return result;
    }

    private _loadFolderContent(folderId, node = null) {
        if (folderId && this.treeListOptions && this.treeListOptions.items) {
            if (!node) {
                node = this._getNodeByFolderId(this.treeListOptions.items, folderId);
                if (!node) {
                    this._router.navigate([], { relativeTo: this._activatedRoute });
                }
            }
            if (this._isWorkspace(node) === false) {
                const action = new IntralinksLoadFolderContent();
                this.workspaceId = node.workspaceId;
                action.payload = {
                    screenId: this.id,
                    workspaceId: node.workspaceId,
                    folderId: folderId,
                    mapping: _.cloneDeep(this.data.folderMappings)
                };
                this._store.dispatch(action);
                this.loading.content = true;
            } else {
                this.loading.content = true;
                // this.files = this._generateFilesInWorkspace(this.treeListOptions.items, folderId);
                const files = this._generateFilesInWorkspace(this.treeListOptions.items, folderId);
                const payload = new IntralinksContentContainerReloadActionPayload(IntralinksContentType.FOLDER, files);
                const action = new IntralinksContentContainerReloadAction(this.id, this.folderId, payload);
                this.contentContainerConfig.actionSubject$.next(action);
                this.loading.content = false;
                // this._hightlightBySelector((item) => item.parent && item.parent.data.folderId === folderId && item.index === 0);
            }
            this._highlightNode(folderId);
        }
    }

    private _addRecentDocumentsOption(folders, isActive) {
        let result = [];
        if (folders && folders.length) {
            result = [{
                displayText: 'Recently Added',
                folderId: null,
                workspaceId: null,
                folderType: 'link',
                createdDate: null,
                createdBy: null,
                lastModifiedDate: null,
                lastModifiedBy: null,
                allowChildren: false,
                isExpanded: false,
                expanded: false,
                link: null,
                isFocused: isActive,
                isActive: isActive,
                isSelected: isActive
            }, ...folders];
            result.forEach(workspace => {
                if (workspace && workspace.items && workspace.items.length) {
                    workspace.items.unshift({
                        displayText: 'Recently Added',
                        folderId: null,
                        workspaceId: workspace.folderId,
                        folderType: 'link',
                        createdDate: workspace.createdDate,
                        createdBy: workspace.createdBy,
                        lastModifiedDate: workspace.lastModifiedDate,
                        lastModifiedBy: workspace.lastModifiedBy,
                        allowChildren: false,
                        isExpanded: false,
                        expanded: false,
                        link: null,
                        isFocused: isActive,
                        isActive: isActive,
                        isSelected: isActive
                    });
                }
            });
        }
        return result;
    }

    /**
     * navigating to folder detail -or- recently added
     */
    private _navigateToFolderDetail() {
        const childParams = this._activatedRoute.snapshot.firstChild.params;
        this.folderId = childParams['folderid'];
        this.timeFilter = childParams['timeFilter'];
        this.keyword = childParams['keyword'];
        const changed = this._parseTimeFilter(childParams['timeFilter']);
        this._initContent(changed);
    }

    private _parseTimeFilter(timeFilter: string) {
        let changed = false;
        if (timeFilter) {
            const units = ['d', 'y', 'm'];
            units.forEach(unit => {
                const value = timeFilter.replace(unit, '');
                if (value !== timeFilter) {
                    changed = true;
                    this.timeUnit = unit;
                    this.timeValue = value;
                }
            });
        } else {
            this.timeUnit = null;
            this.timeValue = null;
        }
        return changed;
    }

    private _initGroupConfig() {
        if (this._feedbackSubscription && !this._feedbackSubscription.closed) {
            this._feedbackSubscription.unsubscribe();
        }
        const groupConfig = new IntralinksGroupContainerConfig();
        groupConfig.folders = [];
        const workspaceId = this.folderId || null;
        let workspaceIds: string[] = null;
        if (workspaceId) {
            // workspace level recents
            if (this.treeListOptions && this.treeListOptions.items) {
                groupConfig.folders = this.treeListOptions.items.filter(item => item.workspaceId === workspaceId);
            }
        } else {
            // account level recents
            groupConfig.folders = this.treeListOptions.items;
            groupConfig.showWorkspaceName = true;
            workspaceIds = groupConfig.folders.map(folder => folder.workspaceId).filter((id) => !!id);
        }
        groupConfig.mappings = this.data.folderMappings;
        this.groupConfig = groupConfig;
        const payload = new IntralinksContentContainerReloadActionPayload(IntralinksContentType.GROUP, groupConfig);
        const action = new IntralinksContentContainerReloadAction(this.id, workspaceId, payload, workspaceIds);
        this.contentContainerConfig.actionSubject$.next(action);

        const reloadAction = new IntralinksGroupContainerReloadAction();
        reloadAction.payload = {
            timeValue: this.timeValue,
            timeUnit: this.timeUnit,
            keyword: this.keyword,
            workspaceId: workspaceId,
            screenId: this.id,
        };
        this._feedbackSubscription = this.groupConfig.feedbackSubject$.subscribe((feedback: IntralinksGroupContainerReadyFeedback) => {
            this.groupConfig.actionSubject$.next(reloadAction);
        });

        this._highlightNode(workspaceId);
        this.loading.content = false;
    }

    private _highlightNode(folderId) {
        if (this.treeListOptions && this.treeListOptions.actionSubject$ && !this._skipNodeHightlight) {
            // need more time for aacl tree list render
            setTimeout(() => {
                // expand the node
                if (folderId) {
                    this._expandToChildNode(folderId);
                }

                // high light the current selected node
                this.treeListOptions.actionSubject$.next({
                    type: EnumAaclTreeListActionType.ActivateNode,
                    payload: (node) => {
                        if (this.timeFilter) {
                            return node.data.folderType === 'link' && node.data.workspaceId === folderId;
                        } else {
                            return node.data.folderId === folderId;
                        }
                    }
                });
            });
        } else {

            this._skipNodeHightlight = false;
        }
    }

    private _expandToChildNode(folderId: string) {
        this.treeListOptions.actionSubject$.next({
            type: EnumAaclTreeListActionType.ExpandToNode,
            payload: (item) => {
                if (item.data.folderId === folderId) {
                    return !item.hasChildren;
                } else {
                    return item.parent && item.parent.data.folderId === folderId && item.index === 0;
                }
            }
        });
    }

    private _initContent(groupFilterChanged = true) {
        // wait until treeListOptions ready
        if (!this.treeListOptions || !this.treeListOptions.items) {
            this._stackedInitContent = true;
        } else {
            if (this.timeFilter && (!this.groupConfig || this.groupConfig.folders)) {
                if (groupFilterChanged) {
                    this._initGroupConfig();
                }
            } else if (this.folderId) {
                this._loadFolderContent(this.folderId);
            }
        }
    }

    private _initAccount(id) {
        if (this.screens && this.screens.length) {
            const screen = this.screens.reduce((pre, cur) => cur.id === id ? cur : pre, null);
            if (screen) {
                this.name = screen.name;
                const accountInfo = new IntralinksAccountInfo(null, null, null, screen.accessList);
                accountInfo.mapFromServer(screen.configuration);
                this.data = accountInfo;
                if (this.screenDeletable !== screen.deletable || this.screenEditable !== screen.editable) {
                    this.screenDeletable = screen.deletable;
                    this.screenEditable = screen.editable;
                    this._initContextMenu();
                } else {
                    this.screenDeletable = screen.deletable;
                    this.screenEditable = screen.editable;
                }
                this._intralinksService.setData$.next(id);
                if (this._stackedInitTree) {
                    this.loading.tree = true;
                    this.treeListOptions = null;
                    const action = new IntralinksLoadWorkspacesFolders();
                    action.payload = {
                        screenId: id
                    };
                    this._store.dispatch(action);
                    this._stackedInitTree = false;
                } else {
                    // after mapping changed, refresh quick submit disability
                    this._initContent();
                }
            } else {
                this._toast(NotificationStyles.Error, 'Unknown account id ' + id);
                this._router.navigate(['/error']);
            }
        } else {
            this._stackedInitTree = true;
        }
    }

    private _initContextMenu() {
        this.contextMenuConfig.items = [
            { id: '0', text: 'Edit', disabled: !this.screenEditable },
            { id: '1', text: 'Delete', disabled: !this.screenDeletable },
        ];
        this._destroySubscriptions.push(this.contextMenuConfig.events$.subscribe(event => this._onMenuSelect(event)));
    }

    private _onMenuSelect(event) {
        switch (event.payload.id) {
            case '0':
                this.editAccount();
                break;
            case '1':
                this.deleteAccount();
                break;
            case '2':
                this.mappingConfiguration();
                break;
            default:
                break;
        }
    }

    showContextMenu(e: MouseEvent) {
        this._changeDetectorRef.detectChanges();
        this.contextMenuConfig.show$.next(new ContextMenuShowAction(e));
    }

    private _splashMfaValidate(data): Promise<any> {
        if (data.isExpanded || !data.allowChildren) {
            return new Promise(resolve => resolve(true));
        } else {
            return this._validationService.validate(this.id, data.workspaceId).pipe(take(1)).toPromise();
        }
    }

    private _collapseAllTreeNodes() {
        if (this.treeListOptions && this.treeListOptions.actionSubject$) {
            this.treeListOptions.actionSubject$.next({
                type: EnumAaclTreeListActionType.CollapseAll,
                payload: null
            });
        }
    }

    private _toast(type: NotificationStyles, msg: string) {
        const note = new NotificationOptions();
        note.style = type;
        if (msg.includes(businessConstants.errorMessage.ERR_INTRALINKS_SERVER)) {
            note.message = StringLiteralsPipe.translate('general.the_configuration_is_not_correct');
        } else {
            note.message = msg;
        }
        this._toastService.notify(note);
    }

}
